import {isRight} from 'fp-ts/lib/Either';
import {Any, Decoder, Errors, TypeOf, Validation} from 'io-ts';

export type LaxValidation<T> = Invalid | Valid<T> | Dirty<T>;
export type Valid<T> = {readonly _tag: 'Valid'; readonly value: T};
export type Dirty<T> = {readonly _tag: 'Dirty'; readonly value: T; readonly errors: Errors};
export type Invalid = {readonly _tag: 'Invalid'; readonly errors: Errors};

export namespace LaxValidation {
    export const fromStrict = <T>(either: Validation<T>): LaxValidation<T> =>
        isRight(either) ? valid(either.right) : invalid(either.left);

    export const valid = <T>(value: T): Valid<T> => ({_tag: 'Valid', value});
    export const invalid = (errors: Errors): Invalid => ({_tag: 'Invalid', errors});
    export const dirty = <T>(value: T, errors: Errors): Dirty<T> => ({_tag: 'Dirty', value, errors});

    export const isValid = <T>(v: LaxValidation<T>): v is Valid<T> => v._tag === 'Valid';
    export const isInvalid = <T>(v: LaxValidation<T>): v is Invalid => v._tag === 'Invalid';
    export const isDirty = <T>(v: LaxValidation<T>): v is Dirty<T> => v._tag === 'Dirty';
}

export function partiallyDecodeAndMutateInvalid<T extends Any>(
    schema: T,
    thing: unknown,
    fallbacks: Map<Decoder<any, any>, any>
): LaxValidation<TypeOf<T>> {
    const decoded = schema.decode(thing);
    if (isRight(decoded)) {
        return LaxValidation.fromStrict(decoded);
    }
    const errors = decoded.left.map(err => ({
        propertyPath: err.context.slice(1).map(c => c.key),
        propertyType: err.context.find(({type}) => fallbacks.has(type))?.type,
    }));
    const hasOtherErrors = !errors.every(err => err.propertyType != null);
    if (hasOtherErrors) {
        return LaxValidation.fromStrict(decoded);
    }
    for (const {propertyPath, propertyType} of errors) {
        setAtPath(propertyPath, thing, fallbacks.get(propertyType!)!);
    }
    const decodedAgain = schema.decode(thing);
    if (isRight(decodedAgain)) {
        return LaxValidation.dirty(decodedAgain.right, decoded.left);
    } else {
        return LaxValidation.invalid(decodedAgain.left);
    }
}

function setAtPath(path: PropertyKey[], object: any, value: any) {
    if (path.length === 0) {
        return false;
    }
    if (!object || typeof object !== 'object') {
        return false;
    }
    const parent = getAtPath(path.slice(0, -1), object);
    if (!parent) return false;
    const lastKey = path.at(-1);
    if (!lastKey) return false;
    parent[lastKey] = value;
    return true;
}

function getAtPath(path: PropertyKey[], thing: any) {
    if (thing == null || typeof thing !== 'object') {
        return undefined;
    }
    if (path.length === 0) {
        return thing;
    }
    const [key, ...rest] = path;
    if (key in thing) {
        if (rest.length === 0) {
            return thing[key];
        } else {
            return getAtPath(rest, thing[key]);
        }
    } else {
        return undefined;
    }
}
